import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Save, Settings, Hash, Clock, Bell, Mail, Plus, X, AlertTriangle, CheckCircle, XCircle, AlertCircle, Send } from "lucide-react";

export default function VoucherConfig() {
  const { axiosAuth } = useAuth();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [confirmSave, setConfirmSave] = useState(false);
  const [emailStatus, setEmailStatus] = useState(null);
  // Iter 129 — envío de prueba
  const [testEmail, setTestEmail] = useState("");
  const [testFlow, setTestFlow] = useState("generic");
  const [testSending, setTestSending] = useState(false);

  const refreshEmailStatus = () => {
    axiosAuth().get("/vouchers/email-status")
      .then(r => setEmailStatus(r.data))
      .catch(() => {});
  };

  const sendTest = async () => {
    if (!testEmail.trim()) {
      toast.error("Falta email destino de prueba");
      return;
    }
    setTestSending(true);
    try {
      const res = await axiosAuth().post("/vouchers/email-test", { to: testEmail.trim(), flow: testFlow });
      if (res.data?.status === "sent") {
        toast.success(`Email de prueba enviado a ${testEmail.trim()}`);
      } else if (res.data?.status === "mock") {
        toast.info(`Email registrado en modo prueba (proveedor no operativo): ${testEmail.trim()}`);
      } else {
        toast.success("Email de prueba procesado");
      }
      refreshEmailStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error enviando email de prueba");
    } finally {
      setTestSending(false);
    }
  };

  useEffect(() => {
    Promise.all([
      axiosAuth().get("/vouchers/config"),
      axiosAuth().get("/vouchers/email-status"),
    ]).then(([cfgRes, emailRes]) => {
      const cfg = cfgRes.data.config || {};
      if (!cfg.reminder_days) cfg.reminder_days = [7, 2];
      if (cfg.reminders_enabled === undefined) cfg.reminders_enabled = false;
      if (!cfg.reminder_email_subject) cfg.reminder_email_subject = "Tu bono Ascauto caduca pronto";
      if (!cfg.reminder_email_body) cfg.reminder_email_body = "Recuerda canjear tu bono antes de que caduque.";
      setConfig(cfg);
      setEmailStatus(emailRes.data);
      setLoading(false);
    }).catch(() => { toast.error("Error al cargar"); setLoading(false); });
  }, [axiosAuth]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axiosAuth().put("/vouchers/config", config);
      toast.success("Configuracion guardada");
      setConfirmSave(false);
    } catch { toast.error("Error al guardar"); }
    finally { setSaving(false); }
  };

  const addReminderDay = () => {
    const days = config.reminder_days || [];
    if (days.length < 5) {
      setConfig(c => ({ ...c, reminder_days: [...days, 3] }));
    }
  };

  const removeReminderDay = (index) => {
    setConfig(c => ({ ...c, reminder_days: c.reminder_days.filter((_, i) => i !== index) }));
  };

  const updateReminderDay = (index, value) => {
    setConfig(c => {
      const days = [...(c.reminder_days || [])];
      days[index] = parseInt(value) || 1;
      return { ...c, reminder_days: days };
    });
  };

  if (loading || !config) return <div className="p-8 text-center text-sm text-slate-400">Cargando...</div>;

  return (
    <div className="space-y-6" data-testid="voucher-config">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="heading-industrial text-2xl sm:text-3xl font-bold text-slate-900 uppercase tracking-tight">Configuracion</h1>
          <p className="text-sm text-slate-500 mt-1">Ajustes generales del modulo de atenciones comerciales</p>
        </div>
        <Button onClick={() => setConfirmSave(true)} disabled={saving}
          className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm text-xs uppercase tracking-wider h-9 px-5 font-semibold" data-testid="save-config-btn">
          <Save className="w-3.5 h-3.5 mr-1.5" /> {saving ? "Guardando..." : "Guardar"}
        </Button>
      </div>

      {/* Numbering */}
      <Card className="border-slate-200 rounded-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-xs uppercase tracking-[0.15em] font-bold text-slate-500 flex items-center gap-2">
            <Hash className="w-4 h-4 text-red-600" /> Numeracion
          </CardTitle>
          <CardDescription className="text-xs text-slate-400">Prefijo y formato de la numeracion de bonos</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.12em] font-bold text-slate-600">Prefijo</Label>
            <p className="text-[10px] text-slate-400 mb-1.5">Formato: PREFIJO-ANNO-SECUENCIA (ej: ASC-ATC-2026-000001)</p>
            <Input value={config.numbering_prefix || "ASC-ATC"} onChange={e => setConfig(c => ({...c, numbering_prefix: e.target.value}))}
              className="rounded-sm h-9 border-slate-200 font-mono w-40" data-testid="numbering-prefix-input" />
          </div>
        </CardContent>
      </Card>

      {/* Default values */}
      <Card className="border-slate-200 rounded-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-xs uppercase tracking-[0.15em] font-bold text-slate-500 flex items-center gap-2">
            <Clock className="w-4 h-4 text-red-600" /> Valores por defecto
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.12em] font-bold text-slate-600">Dias de validez por defecto</Label>
            <Input type="number" value={config.default_expiry_days || 30} onChange={e => setConfig(c => ({...c, default_expiry_days: parseInt(e.target.value) || 30}))}
              className="rounded-sm h-9 border-slate-200 w-24" data-testid="default-expiry-input" />
          </div>
          <div className="flex items-center justify-between border border-slate-200 rounded-sm p-4">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.12em] font-bold text-slate-600">Matricula obligatoria</Label>
              <p className="text-[10px] text-slate-400 mt-0.5">Exigir matricula al emitir un bono.</p>
            </div>
            <Switch checked={config.require_plate || false} onCheckedChange={v => setConfig(c => ({...c, require_plate: v}))} data-testid="require-plate-switch" />
          </div>
          <div className="flex items-center justify-between border border-slate-200 rounded-sm p-4">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.12em] font-bold text-slate-600">Email obligatorio</Label>
              <p className="text-[10px] text-slate-400 mt-0.5">Exigir email del cliente al emitir un bono.</p>
            </div>
            <Switch checked={config.require_email || false} onCheckedChange={v => setConfig(c => ({...c, require_email: v}))} data-testid="require-email-switch" />
          </div>
        </CardContent>
      </Card>

      {/* Email system status */}
      {emailStatus && (
        <Card className={`rounded-sm ${emailStatus.status === "active" ? "border-emerald-200" : emailStatus.status === "mock" ? "border-amber-200" : "border-red-200"}`} data-testid="email-status-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-xs uppercase tracking-[0.15em] font-bold flex items-center gap-2"
              style={{ color: emailStatus.status === "active" ? "#059669" : emailStatus.status === "mock" ? "#d97706" : "#dc2626" }}>
              {emailStatus.status === "active" ? <CheckCircle className="w-4 h-4" /> : emailStatus.status === "mock" ? <AlertCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
              Estado del sistema de email
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className={`p-4 rounded-sm ${emailStatus.status === "active" ? "bg-emerald-50" : emailStatus.status === "mock" ? "bg-amber-50" : "bg-red-50"}`}>
              <p className={`text-sm font-semibold ${emailStatus.status === "active" ? "text-emerald-800" : emailStatus.status === "mock" ? "text-amber-800" : "text-red-800"}`}
                 data-testid="email-status-headline">
                {emailStatus.status === "active"
                  ? "Email configurado correctamente"
                  : emailStatus.status === "mock"
                    ? "Email en modo prueba"
                    : "Falta configuración"}
              </p>
              <p className="text-xs text-slate-600 mt-1" data-testid="email-status-message">{emailStatus.message}</p>
              {emailStatus.provider_label && (
                <p className="text-[11px] text-slate-500 mt-2">
                  Proveedor activo: <strong data-testid="email-status-provider">{emailStatus.provider_label}</strong>
                  {emailStatus.secondary_available && <span className="ml-2 text-slate-400">(respaldo disponible)</span>}
                </p>
              )}
              {emailStatus.sender_email && (
                <div className="flex flex-wrap gap-3 text-[11px] text-slate-500 mt-1">
                  <span>Remitente: <strong>{emailStatus.sender_name || "—"}</strong></span>
                  <span>&lt;{emailStatus.sender_email}&gt;</span>
                </div>
              )}
              {Array.isArray(emailStatus.issues) && emailStatus.issues.length > 0 && (
                <ul className="mt-2 text-[11px] text-red-700 list-disc list-inside space-y-0.5" data-testid="email-status-issues">
                  {emailStatus.issues.map((it, i) => <li key={i}>{it}</li>)}
                </ul>
              )}
              {emailStatus.status !== "active" && (
                <p className="text-[10px] text-slate-400 mt-2 border-t border-slate-200 pt-2">
                  Configura el proveedor en <strong>Centro de Comunicaciones → Remitentes</strong> o ajusta las variables de entorno (RESEND_API_KEY / SENDER_EMAIL o SMTP_HOST + SMTP_PASSWORD).
                </p>
              )}
            </div>
            <div className="mt-3 space-y-1">
              <p className="text-[10px] uppercase tracking-[0.12em] font-bold text-slate-400">Flujos que usan este sistema</p>
              <div className="flex flex-wrap gap-2">
                {["Envio de PDF por email", "Confirmacion de canje", "Recordatorios de caducidad"].map(f => (
                  <span key={f} className="text-[10px] bg-slate-100 text-slate-600 px-2 py-1 rounded-sm">{f}</span>
                ))}
              </div>
            </div>
            {/* Test email send */}
            <div className="mt-4 border-t border-slate-200 pt-3" data-testid="email-test-block">
              <p className="text-[10px] uppercase tracking-[0.12em] font-bold text-slate-500 mb-2">Enviar prueba</p>
              <div className="grid grid-cols-1 sm:grid-cols-[1fr_180px_auto] gap-2">
                <Input
                  type="email"
                  placeholder="email-destino@ejemplo.com"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  className="rounded-sm h-9 border-slate-200"
                  data-testid="email-test-to"
                />
                <select
                  className="border border-slate-200 rounded-sm h-9 text-xs px-2"
                  value={testFlow}
                  onChange={(e) => setTestFlow(e.target.value)}
                  data-testid="email-test-flow"
                >
                  <option value="generic">Prueba genérica</option>
                  <option value="pdf">PDF de bono</option>
                  <option value="redeem">Confirmación de canje</option>
                  <option value="reminder">Recordatorio de caducidad</option>
                </select>
                <Button
                  onClick={sendTest}
                  disabled={testSending || !testEmail.trim()}
                  className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm text-xs uppercase font-semibold h-9 px-4"
                  data-testid="email-test-send-btn"
                >
                  <Send className={`w-3.5 h-3.5 mr-1 ${testSending ? "animate-pulse" : ""}`} />
                  {testSending ? "Enviando..." : "Enviar prueba"}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Expiry Reminders */}
      <Card className="border-slate-200 rounded-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-xs uppercase tracking-[0.15em] font-bold text-slate-500 flex items-center gap-2">
            <Bell className="w-4 h-4 text-amber-600" /> Recordatorios de caducidad
          </CardTitle>
          <CardDescription className="text-xs text-slate-400">Envia recordatorios automaticos por email a clientes con bonos proximos a caducar</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between border border-slate-200 rounded-sm p-4">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.12em] font-bold text-slate-600">Activar recordatorios</Label>
              <p className="text-[10px] text-slate-400 mt-0.5">El sistema enviara emails automaticos a los clientes cuyos bonos esten a punto de caducar.</p>
            </div>
            <Switch checked={config.reminders_enabled || false} onCheckedChange={v => setConfig(c => ({...c, reminders_enabled: v}))} data-testid="reminders-enabled-switch" />
          </div>

          {config.reminders_enabled && (
            <>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.12em] font-bold text-slate-600 mb-2 block">Dias de antelacion</Label>
                <p className="text-[10px] text-slate-400 mb-3">Se enviara un recordatorio para cada hito configurado. Maximo 5 hitos.</p>
                <div className="space-y-2">
                  {(config.reminder_days || []).map((day, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <Input type="number" min="1" max="90" value={day} onChange={e => updateReminderDay(i, e.target.value)}
                        className="rounded-sm h-9 border-slate-200 w-20 font-mono" data-testid={`reminder-day-${i}`} />
                      <span className="text-xs text-slate-500">dias antes de caducidad</span>
                      {(config.reminder_days || []).length > 1 && (
                        <Button variant="ghost" size="sm" onClick={() => removeReminderDay(i)} className="h-7 w-7 p-0 text-slate-400 hover:text-red-600">
                          <X className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
                {(config.reminder_days || []).length < 5 && (
                  <Button variant="outline" size="sm" onClick={addReminderDay} className="mt-2 rounded-sm text-[10px] uppercase tracking-wider h-7 px-3" data-testid="add-reminder-day-btn">
                    <Plus className="w-3 h-3 mr-1" /> Anadir hito
                  </Button>
                )}
              </div>

              <div>
                <Label className="text-[10px] uppercase tracking-[0.12em] font-bold text-slate-600">Asunto del email</Label>
                <Input value={config.reminder_email_subject || ""} onChange={e => setConfig(c => ({...c, reminder_email_subject: e.target.value}))}
                  placeholder="Tu bono Ascauto caduca pronto" className="rounded-sm h-9 border-slate-200 mt-1" data-testid="reminder-subject-input" />
              </div>

              <div>
                <Label className="text-[10px] uppercase tracking-[0.12em] font-bold text-slate-600">Mensaje adicional (opcional)</Label>
                <Textarea value={config.reminder_email_body || ""} onChange={e => setConfig(c => ({...c, reminder_email_body: e.target.value}))}
                  placeholder="Recuerda canjear tu bono antes de que caduque." className="rounded-sm border-slate-200 mt-1" rows={2} data-testid="reminder-body-input" />
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Permissions */}
      <Card className="border-slate-200 rounded-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-xs uppercase tracking-[0.15em] font-bold text-slate-500 flex items-center gap-2">
            <Settings className="w-4 h-4 text-red-600" /> Permisos
          </CardTitle>
          <CardDescription className="text-xs text-slate-400">Los permisos se gestionan desde el rol de cada usuario.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-xs text-slate-600 space-y-2">
            <p><strong className="text-slate-800">super_admin / admin:</strong> Acceso total (plantillas, emision, canje, informes, config)</p>
            <p><strong className="text-slate-800">responsable_centro:</strong> Emitir, canjear, ver dashboard e informes de su centro</p>
            <p><strong className="text-slate-800">asesor_autorizado:</strong> Crear y emitir bonos</p>
            <p><strong className="text-slate-800">recepcion:</strong> Consultar y canjear bonos</p>
            <p className="text-[10px] text-slate-400 pt-2">Solo admin y super_admin pueden crear o editar plantillas y anular bonos.</p>
          </div>
        </CardContent>
      </Card>

      {/* Save confirmation dialog */}
      <Dialog open={confirmSave} onOpenChange={o => { if (!o) setConfirmSave(false); }}>
        <DialogContent className="max-w-sm rounded-sm" data-testid="save-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="text-base font-bold flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600" /> Confirmar cambios
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-600 pt-2">
              Vas a guardar los cambios de configuracion del modulo de atenciones comerciales.
              {config.reminders_enabled && " Los recordatorios automaticos estan activados."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="pt-2">
            <Button variant="outline" onClick={() => setConfirmSave(false)} className="rounded-sm">Cancelar</Button>
            <Button onClick={handleSave} disabled={saving}
              className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm" data-testid="confirm-save-btn">
              {saving ? "Guardando..." : "Si, guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
